import { Delete as DeleteIcon } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { Box, Grid, IconButton, Tooltip } from '@mui/material';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, UseFieldArrayRemove, UseFormReturn } from 'react-hook-form';
import { FormDropdown } from 'src/components/FormPart/FormDropdown';
import { SelectOption } from 'src/UIKit';
import { ContactDefaultCustomField } from 'src/constants/types';
import {
  dateRuleOperatorOptions,
  DYNAMIC_GROUP_GROUP_ID_FIELD,
  groupIdRuleOperatorOptions,
  hasEmptyRuleValue,
  normalizeRuleValues,
  ruleOperatorOptions,
} from '../../helpers';
import { Group, GroupFormData } from '../../types';
import { DateRuleValuesInput } from './DateRuleValuesInput';
import { GroupIdRuleValuesInput } from './GroupIdRuleValuesInput';

type DynamicGroupRulePartProps = {
  form: UseFormReturn<GroupFormData>;
  fieldIndex: number;
  customFields: ContactDefaultCustomField[];
  customGroups: Group[];
  remove: UseFieldArrayRemove;
};

type RuleValuesInputProps = {
  form: UseFormReturn<GroupFormData>;
  fieldName: `rules.${number}.values`;
};

type RulePresetOption = {
  title: string;
  value: string;
  values: string[];
};

const CONTACT_JOIN_TYPE_FIELD_NAME = 'contact.standard.opt_in_type';
const CONTACT_JOIN_TYPE_OPERATOR = '=';
const contactJoinTypeRuleOptions: RulePresetOption[] = [
  { title: 'Manually Added', value: 'manually_added', values: ['company', 'api'] },
  { title: 'Texted In', value: 'texted_in', values: ['contact'] },
];

const areRuleValuesEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const RuleValuesInput: FC<RuleValuesInputProps> = ({ form, fieldName }) => {
  const [inputValue, setInputValue] = useState('');

  return (
    <Controller<GroupFormData, `rules.${number}.values`>
      name={fieldName}
      control={form.control}
      rules={{
        validate: value =>
          normalizeRuleValues(Array.isArray(value) ? value : []).length > 0 ||
          'Value is required',
      }}
      render={({ field, fieldState }) => {
        const currentValues = normalizeRuleValues(Array.isArray(field.value) ? field.value : []);
        const setValues = (nextValues: readonly string[]) => {
          form.setValue(fieldName, normalizeRuleValues(nextValues), {
            shouldDirty: true,
            shouldValidate: true,
          });
        };
        const commitInputValue = () => {
          if (!inputValue.trim()) {
            return;
          }

          setValues([...currentValues, inputValue]);
          setInputValue('');
        };

        return (
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            options={[]}
            value={currentValues}
            inputValue={inputValue}
            onInputChange={(_, value) => setInputValue(value)}
            onChange={(_, values) => {
              setValues(values);
              setInputValue('');
            }}
            onBlur={() => {
              commitInputValue();
              field.onBlur();
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });

                return (
                  <Box key={key} component="span" display="inline-flex" alignItems="center" mr={0.5}>
                    <Chip label={option} size="small" {...tagProps} />
                  </Box>
                );
              })
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Values"
                placeholder={currentValues.length ? '' : 'Enter Values'}
                margin="dense"
                required
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        );
      }}
    />
  );
};

const ContactJoinTypeRuleValuesInput: FC<RuleValuesInputProps> = ({ form, fieldName }) => {
  return (
    <Controller<GroupFormData, `rules.${number}.values`>
      name={fieldName}
      control={form.control}
      rules={{
        validate: value => {
          const normalizedValues = normalizeRuleValues(Array.isArray(value) ? value : []);
          return (
            contactJoinTypeRuleOptions.some(option =>
              areRuleValuesEqual(option.values, normalizedValues)
            ) || 'Value is required'
          );
        },
      }}
      render={({ field, fieldState }) => {
        const currentValues = normalizeRuleValues(Array.isArray(field.value) ? field.value : []);
        const selectedOption = contactJoinTypeRuleOptions.find(option =>
          areRuleValuesEqual(option.values, currentValues)
        );
        const selectedOptionValue = selectedOption?.value ?? '';

        return (
          <TextField
            select
            label="Values"
            value={selectedOptionValue}
            required
            margin="dense"
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            onChange={event => {
              const option = contactJoinTypeRuleOptions.find(item => item.value === event.target.value);
              field.onChange(option?.values ?? []);
            }}
            onBlur={field.onBlur}
          >
            {contactJoinTypeRuleOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.title}
              </MenuItem>
            ))}
          </TextField>
        );
      }}
    />
  );
};

export const DynamicGroupRulePart: FC<DynamicGroupRulePartProps> = props => {
  const { form, fieldIndex, customFields, customGroups, remove } = props;

  const selectedFieldName = form.watch(`rules.${fieldIndex}.fieldName`);
  const operator = form.watch(`rules.${fieldIndex}.operator`);
  const valuesFieldName = `rules.${fieldIndex}.values` as const;
  const values = form.watch(valuesFieldName) || [];
  const isContactJoinTypeField = selectedFieldName === CONTACT_JOIN_TYPE_FIELD_NAME;
  const isGroupIdField = selectedFieldName === DYNAMIC_GROUP_GROUP_ID_FIELD;
  const selectedFieldType = customFields.find(field => field.name === selectedFieldName)?.type;
  const isDateField = selectedFieldType === 'date' || selectedFieldType === 'datetime';
  const isDateTimeField = selectedFieldType === 'datetime';
  const previousFieldNameRef = useRef(selectedFieldName);

  const operatorOptions = isGroupIdField
    ? groupIdRuleOperatorOptions
    : isDateField
      ? dateRuleOperatorOptions
      : ruleOperatorOptions;

  const customFieldOptions: SelectOption[] = useMemo(() => {
    return [
      ...customFields.map(f => ({
        title: f.description,
        value: f.name,
        group: 'Contact Fields',
      })),
      {
        title: 'Group Membership',
        value: DYNAMIC_GROUP_GROUP_ID_FIELD,
        group: 'Groups',
      },
    ];
  }, [customFields]);

  useEffect(() => {
    if (!isContactJoinTypeField) {
      return;
    }

    if (operator !== CONTACT_JOIN_TYPE_OPERATOR) {
      form.setValue(`rules.${fieldIndex}.operator`, CONTACT_JOIN_TYPE_OPERATOR, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [fieldIndex, form, isContactJoinTypeField, operator]);

  useEffect(() => {
    if (!isGroupIdField) {
      return;
    }

    if (operator !== '=' && operator !== '!=') {
      form.setValue(`rules.${fieldIndex}.operator`, '=', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [fieldIndex, form, isGroupIdField, operator]);

  useEffect(() => {
    if (!isDateField || isContactJoinTypeField || isGroupIdField) {
      return;
    }

    if (operator === '>=') {
      form.setValue(`rules.${fieldIndex}.operator`, '>', {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    if (operator === '<=') {
      form.setValue(`rules.${fieldIndex}.operator`, '<', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [fieldIndex, form, isContactJoinTypeField, isDateField, isGroupIdField, operator]);

  useEffect(() => {
    const previousFieldName = previousFieldNameRef.current;

    if (previousFieldName !== selectedFieldName) {
      const switchedToOrFromGroupId =
        previousFieldName === DYNAMIC_GROUP_GROUP_ID_FIELD ||
        selectedFieldName === DYNAMIC_GROUP_GROUP_ID_FIELD;

      if (switchedToOrFromGroupId) {
        form.setValue(valuesFieldName, [], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      previousFieldNameRef.current = selectedFieldName;
    }
  }, [form, selectedFieldName, valuesFieldName]);

  useEffect(() => {
    if (hasEmptyRuleValue(operator)) {
      form.unregister(valuesFieldName, { keepValue: true, keepDirty: true, keepTouched: true });
      return;
    }

    if (isContactJoinTypeField || isGroupIdField) {
      return;
    }

    if (!values.length) {
      form.setValue(valuesFieldName, [''], { shouldDirty: true });
    }
  }, [form, isContactJoinTypeField, isGroupIdField, operator, values.length, valuesFieldName]);

  const hasValueInputs = !hasEmptyRuleValue(operator);

  return (
    <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
      <Grid item xs={4}>
        <FormDropdown
          form={form}
          fieldName={`rules.${fieldIndex}.fieldName`}
          label="Field"
          placeholder="Enter Field"
          options={customFieldOptions}
          validation={{
            required: 'Field is required',
          }}
          margin="dense"
        />
      </Grid>

      <Grid item xs={2.5}>
        <FormDropdown
          form={form}
          fieldName={`rules.${fieldIndex}.operator`}
          label="Operator"
          placeholder="Enter Operator"
          options={operatorOptions}
          disabled={isContactJoinTypeField}
          validation={{
            required: 'Operator is required',
          }}
          margin="dense"
        />
      </Grid>

      <Grid item xs={4.5}>
        {hasValueInputs ? (
          isContactJoinTypeField ? (
            <ContactJoinTypeRuleValuesInput form={form} fieldName={valuesFieldName} />
          ) : isGroupIdField ? (
            <GroupIdRuleValuesInput
              form={form}
              fieldName={valuesFieldName}
              customGroups={customGroups}
            />
          ) : isDateField ? (
            <DateRuleValuesInput
              form={form}
              fieldName={valuesFieldName}
              includeTime={isDateTimeField}
            />
          ) : (
            <RuleValuesInput form={form} fieldName={valuesFieldName} />
          )
        ) : (
          <TextField
            label="Value"
            placeholder="Enter Value"
            value=""
            disabled
            margin="dense"
            fullWidth
          />
        )}
      </Grid>

      <Grid item xs={1}>
        <Tooltip title="Remove rule">
          <IconButton
            aria-label="Remove rule"
            onClick={() => remove(fieldIndex)}
            sx={{ mt: 1 }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};
